import React, { useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import classNames from "classnames";
import type { Placement } from "@popperjs/core";

import Icon from "../Icon/Icon";
import styles from "./FloorSelector.module.scss";

type LatLng = {
  latitude: number;
  longitude: number;
};

export interface Floor {
  id: number;
  floor: string;
  name: string;
  short_name: string;
  default: boolean;
}

export interface Bookmark {
  bearing: number;
  center: LatLng;
  floor: Floor;
  name: string;
  zoom: number;
}

export interface FloorsWithBuildingName {
  [key: string]: Floor[];
}

interface Props {
  dataQA: string;
  options: Floor[] | FloorsWithBuildingName;
  currentFloor: Floor | null;
  currentUserFloor: Floor | null;
  bookmarks?: Bookmark[];
  placement?: Placement;
  maxHeight?: number;
  minWidth?: number;
  className?: string;
  theme?: "light" | "dark";
  onFloorSelect: (floor: Floor) => void;
  onBookmarkSelect?: (bookmark: Bookmark) => void;
  onClick?: () => void;
  findABuildingPlaceholder?: string;
}

const FloorSelector: React.FC<Props> = ({
  dataQA,
  options,
  currentFloor,
  currentUserFloor,
  bookmarks,
  placement = "top",
  minWidth = 200,
  maxHeight,
  className,
  theme = "light",
  onFloorSelect,
  onBookmarkSelect,
  onClick,
  findABuildingPlaceholder = "Find a building",
}) => {
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles: popperStyles, attributes } = usePopper(
    buttonElement,
    popperElement,
    {
      placement,
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["auto"],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          },
        },
      ],
    },
  );

  const [maxHeights, setMaxHeights] = useState<number[]>([]);

  const handleSelect = (value: Floor) => {
    onFloorSelect(value);
  };

  const handleSectionClick = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    index: number,
  ) => {
    const maxHeightsCopy = [...maxHeights];
    maxHeightsCopy[index] = !maxHeightsCopy[index]
      ? e.currentTarget.nextElementSibling?.firstElementChild?.clientHeight || 0
      : 0;

    setMaxHeights(maxHeightsCopy);
  };

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, styles[theme], className)}
    >
      <Popover data-qa="popover-wrapper">
        {({ close }) => (
          <>
            <Popover.Button
              ref={setButtonElement}
              className={styles.button}
              onClick={onClick}
              as={"a"}
            >
              {currentFloor && (
                <span className={styles.buttonText}>
                  {currentFloor.id === currentUserFloor?.id && (
                    <div className={styles.currentUserButtonDot}></div>
                  )}
                  {currentFloor.name}
                </span>
              )}
              {!currentFloor &&
                bookmarks &&
                bookmarks.length > 0 &&
                Array.isArray(options) &&
                options.length === 0 && (
                  <span className={styles.buttonText}>
                    {findABuildingPlaceholder}
                  </span>
                )}
              <Icon dataQA="icon" type="UnfoldMoreIcon" />
            </Popover.Button>

            <Transition
              enter={styles.enter}
              enterFrom={styles.enterFrom}
              enterTo={styles.enterTo}
              leave={styles.leave}
              leaveFrom={styles.leaveFrom}
              leaveTo={styles.leaveTo}
            >
              <Popover.Panel
                ref={setPopperElement}
                style={{
                  ...(maxHeight && { maxHeight: `${maxHeight}px` }),
                  minWidth: `${minWidth}px`,
                  ...popperStyles.popper,
                }}
                className={styles.panel}
                {...attributes.popper}
                onMouseOver={(
                  e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                ) => {
                  e.stopPropagation();
                }}
                onMouseLeave={(
                  e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                ) => {
                  e.stopPropagation();
                }}
              >
                {Array.isArray(options) ? (
                  <>
                    <ul
                      data-qa="floor-selector-list"
                      className={styles.dropdown}
                    >
                      {options.map((floor) => (
                        <li
                          data-qa="floor-selector-list-item"
                          key={floor.name}
                          onClick={() => {
                            handleSelect(floor);
                            close();
                          }}
                          className={classNames(styles.dropdownItem, {
                            [styles.selected]:
                              currentFloor && currentFloor.id === floor.id,
                            [styles.noUserFloor]: !currentUserFloor,
                          })}
                        >
                          {floor.id === currentUserFloor?.id && (
                            <div className={styles.currentUserDot}></div>
                          )}
                          <span>{floor.name}</span>
                        </li>
                      ))}
                    </ul>
                    {bookmarks && (
                      <>
                        {options.length > 0 && (
                          <span className={styles.spacer} />
                        )}
                        <ul
                          data-qa="floor-selector-bookmarks-list"
                          className={classNames(styles.dropdown)}
                        >
                          {bookmarks.length > 0 &&
                            bookmarks.map((bookmark) => (
                              <li
                                data-qa="floor-selector-bookmark-item"
                                key={bookmark.name}
                                onClick={() => {
                                  onBookmarkSelect?.(bookmark);
                                  close();
                                }}
                                className={classNames(
                                  styles.dropdownItem,
                                  styles.bookmarkItem,
                                  {
                                    [styles.noUserFloor]: !currentUserFloor,
                                  },
                                )}
                              >
                                <span>{bookmark.name}</span>
                                <div className={styles.bookmarkIcon}>
                                  <Icon
                                    dataQA="bookmark-icon"
                                    type="LocationIcon"
                                  />
                                </div>
                              </li>
                            ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {Object.keys(options).map((buildingName, index) => {
                      return (
                        <ul
                          data-qa="floor-selector-building-name-list"
                          key={buildingName}
                          className={classNames(
                            styles.dropdown,
                            styles.buildingDropdown,
                          )}
                        >
                          <li data-qa="floor-selector-building-name">
                            <span
                              onClick={(e) => handleSectionClick(e, index)}
                              className={classNames(
                                styles.dropdownItem,
                                styles.buildingName,
                              )}
                            >
                              <Icon
                                dataQA="icon"
                                type="ChevronDownIcon"
                                className={classNames(styles.chevronIcon, {
                                  [styles.rotated]: !!maxHeights[index],
                                })}
                              />
                              {buildingName}
                            </span>
                            <div
                              data-qa="floor-selector-list-container"
                              style={{ maxHeight: maxHeights[index] || 0 }}
                              className={styles.dropdownWrapper}
                            >
                              <ul
                                data-qa="floor-selector-list"
                                className={styles.dropdown}
                              >
                                {options[buildingName].map((floor) => (
                                  <li
                                    data-qa="floor-selector-list-item"
                                    key={floor.name}
                                    onClick={() => {
                                      handleSelect(floor);
                                      close();
                                    }}
                                    className={classNames(styles.dropdownItem, {
                                      [styles.selected]:
                                        currentFloor &&
                                        currentFloor.id === floor.id,
                                    })}
                                  >
                                    {floor.id === currentUserFloor?.id && (
                                      <div
                                        className={styles.currentUserDot}
                                      ></div>
                                    )}
                                    <span>{floor.name}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </li>
                        </ul>
                      );
                    })}
                  </>
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default FloorSelector;
